import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Building2, Plus, Edit, Trash2, Upload, FileText, Search, Sparkles, Eye, ShieldCheck, Pause, Play } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Vendors = () => {
  const { user, hasTabAccess } = useAuth();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [pendingVendorDoc, setPendingVendorDoc] = useState(null); // File object to attach as KYC after create
  const [searchTerm, setSearchTerm] = useState('');
  // KYC status filter: 'all' | 'complete' | 'partial' | 'pending'
  const [kycFilter, setKycFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    name: '',
    pan: '',
    gstin: '',
    bank_account_number: '',
    ifsc_code: '',
    email: '',
    phone: '',
    address: '',
    remarks: ''
  });

  // Stats for filter chips (counts before search is applied)
  const kycCounts = vendors.reduce(
    (acc, v) => {
      const s = v.kyc_status || 'pending';
      acc[s] = (acc[s] || 0) + 1;
      acc.all += 1;
      return acc;
    },
    { all: 0, complete: 0, partial: 0, pending: 0 }
  );

  // Filter vendors based on search term + kyc status
  const filteredVendors = vendors.filter(vendor => {
    if (kycFilter !== 'all') {
      const status = vendor.kyc_status || 'pending';
      if (status !== kycFilter) return false;
    }
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      vendor.name?.toLowerCase().includes(term) ||
      vendor.pan?.toLowerCase().includes(term) ||
      vendor.gstin?.toLowerCase().includes(term) ||
      vendor.email?.toLowerCase().includes(term) ||
      vendor.phone?.toLowerCase().includes(term) ||
      vendor.remarks?.toLowerCase().includes(term)
    );
  });

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/vendors`, {
        withCredentials: true,
      });
      setVendors(response.data);
    } catch (error) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or PDF file');
      return;
    }

    setExtracting(true);
    setUploading(true);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);

      // Upload file to backend for OCR processing
      const response = await axios.post(`${API}/vendors/extract-from-invoice`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Populate form with extracted data
      setFormData({
        name: response.data.name || '',
        pan: response.data.pan || '',
        gstin: response.data.gstin || '',
        bank_account_number: response.data.bank_account_number || '',
        ifsc_code: response.data.ifsc_code || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        address: response.data.address || '',
        remarks: response.data.remarks || ''
      });

      // Hold onto the original file so we can attach it to the vendor's KYC
      // documents once the user clicks Save.
      setPendingVendorDoc(file);

      toast.success('Vendor details extracted! Please review and submit.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extract vendor details');
    } finally {
      setExtracting(false);
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingVendor) {
        await axios.put(`${API}/vendors/${editingVendor.id}`, formData, {
          withCredentials: true,
        });
        toast.success('Vendor updated successfully');
      } else {
        const createRes = await axios.post(`${API}/vendors`, formData, {
          withCredentials: true,
        });
        toast.success('Vendor created successfully');
        // Auto-attach the original document the user uploaded for extraction
        const newVendorId = createRes.data?.id || createRes.data?.vendor_id;
        if (newVendorId && pendingVendorDoc) {
          try {
            const fd = new FormData();
            fd.append('file', pendingVendorDoc);
            await axios.post(
              `${API}/vendors/${newVendorId}/kyc?category=other&label=${encodeURIComponent('Source document (AI extracted)')}`,
              fd,
              {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
              }
            );
            toast.success('Source document saved on vendor');
          } catch (attachErr) {
            console.warn('Failed to attach vendor source document', attachErr);
            toast.error('Vendor created but failed to attach source document');
          }
        }
      }

      setDialogOpen(false);
      resetForm();
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (vendor) => {
    setEditingVendor(vendor);
    setFormData({
      name: vendor.name,
      pan: vendor.pan || '',
      gstin: vendor.gstin || '',
      bank_account_number: vendor.bank_account_number || '',
      ifsc_code: vendor.ifsc_code || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: vendor.address || '',
      remarks: vendor.remarks || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;

    try {
      await axios.delete(`${API}/vendors/${vendorId}`, {
        withCredentials: true,
      });
      toast.success('Vendor deleted successfully');
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggleHold = async (vendor) => {
    const goingOnHold = !vendor.on_hold;
    let reason = vendor.on_hold_reason || '';
    if (goingOnHold) {
      const entered = window.prompt(
        `Put "${vendor.name}" on hold? Their invoices will still appear in approval lists but cannot be selected.\n\nOptional reason:`,
        ''
      );
      if (entered === null) return; // user cancelled
      reason = entered;
    } else if (!window.confirm(`Release "${vendor.name}" from hold? Their invoices can be selected for approval again.`)) {
      return;
    }
    try {
      await axios.patch(
        `${API}/vendors/${vendor.id}/hold`,
        { on_hold: goingOnHold, reason },
        { withCredentials: true }
      );
      toast.success(goingOnHold ? `${vendor.name} placed on hold` : `${vendor.name} released from hold`);
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update hold status');
    }
  };

  const resetForm = () => {
    setEditingVendor(null);
    setPendingVendorDoc(null);
    setFormData({
      name: '',
      pan: '',
      gstin: '',
      bank_account_number: '',
      ifsc_code: '',
      email: '',
      phone: '',
      address: '',
      remarks: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="vendors-page" className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight">
            Vendor Database
          </h1>
          <p className="text-muted-foreground mt-1">Manage vendor information and details</p>
        </div>
        <div className="flex gap-3">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search vendors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 w-64"
              data-testid="search-vendor-input"
            />
          </div>
          {hasTabAccess('vendors') && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-vendor-button" data-write-only onClick={resetForm}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Vendor
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingVendor ? 'Edit' : 'Add'} Vendor</DialogTitle>
                  <DialogDescription>
                    Enter vendor details manually or upload an invoice to auto-extract information
                  </DialogDescription>
                </DialogHeader>

                <Tabs defaultValue="manual" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="manual">Manual Entry</TabsTrigger>
                    <TabsTrigger value="upload" className="flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5" />
                      Upload &amp; Auto-fill (AI)
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="manual" className="space-y-4 mt-4">
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2 col-span-2">
                          <Label htmlFor="name">Vendor Name *</Label>
                          <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            placeholder="Enter vendor name"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="pan">PAN</Label>
                          <Input
                            id="pan"
                            value={formData.pan}
                            onChange={(e) => setFormData({...formData, pan: e.target.value.toUpperCase()})}
                          placeholder="ABCDE1234F"
                          maxLength={10}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="gstin">GSTIN</Label>
                        <Input
                          id="gstin"
                          value={formData.gstin}
                          onChange={(e) => setFormData({...formData, gstin: e.target.value.toUpperCase()})}
                          placeholder="22AAAAA0000A1Z5"
                          maxLength={15}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bank_account">Bank Account Number</Label>
                        <Input
                          id="bank_account"
                          value={formData.bank_account_number}
                          onChange={(e) => setFormData({...formData, bank_account_number: e.target.value})}
                          placeholder="Enter account number"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ifsc">IFSC Code</Label>
                        <Input
                          id="ifsc"
                          value={formData.ifsc_code}
                          onChange={(e) => setFormData({...formData, ifsc_code: e.target.value.toUpperCase()})}
                          placeholder="SBIN0001234"
                          maxLength={11}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="vendor@example.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          placeholder="+91 9876543210"
                        />
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label htmlFor="address">Address</Label>
                        <textarea
                          id="address"
                          value={formData.address}
                          onChange={(e) => setFormData({...formData, address: e.target.value})}
                          placeholder="Registered office address"
                          className="w-full min-h-[60px] p-3 border rounded-md text-sm"
                        />
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label htmlFor="remarks">Remarks</Label>
                        <textarea
                          id="remarks"
                          value={formData.remarks}
                          onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                          placeholder="Additional notes about the vendor"
                          className="w-full min-h-[80px] p-3 border rounded-md text-sm"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit">
                        {editingVendor ? 'Update' : 'Create'} Vendor
                      </Button>
                    </DialogFooter>
                  </form>
                </TabsContent>

                <TabsContent value="upload" className="space-y-4 mt-4">
                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                      <Input
                        id="invoice-upload"
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp,.pdf,application/pdf,image/*"
                        onChange={handleFileUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                      <label
                        htmlFor="invoice-upload"
                        className="cursor-pointer flex flex-col items-center gap-3"
                      >
                        <div className="p-4 bg-gradient-to-br from-[#00204F]/10 to-[#FF7F00]/10 rounded-full">
                          {extracting ? (
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                          ) : (
                            <Sparkles className="h-8 w-8 text-[#FF7F00]" strokeWidth={1.5} />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {extracting
                              ? 'AI is reading your document…'
                              : 'Click to upload an invoice or vendor document'}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            PDF, JPG or PNG up to 10MB · GST cert, PAN, letterhead, or any invoice
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-1">
                            We&apos;ll auto-fill the vendor name, GSTIN, PAN, bank details, email
                            &amp; phone — review &amp; click <strong>Create Vendor</strong> to save.
                          </p>
                        </div>
                      </label>
                    </div>

                    {Object.values(formData).some(val => val) && (
                      <div className="space-y-4">
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm font-medium text-blue-900 mb-2">
                            Extracted Information
                          </p>
                          <p className="text-xs text-blue-700">
                            Review the details below and make any corrections before saving.
                          </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2 col-span-2">
                              <Label htmlFor="name-upload">Vendor Name *</Label>
                              <Input
                                id="name-upload"
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="pan-upload">PAN</Label>
                              <Input
                                id="pan-upload"
                                value={formData.pan}
                                onChange={(e) => setFormData({...formData, pan: e.target.value.toUpperCase()})}
                                maxLength={10}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="gstin-upload">GSTIN</Label>
                              <Input
                                id="gstin-upload"
                                value={formData.gstin}
                                onChange={(e) => setFormData({...formData, gstin: e.target.value.toUpperCase()})}
                                maxLength={15}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="bank-upload">Bank Account</Label>
                              <Input
                                id="bank-upload"
                                value={formData.bank_account_number}
                                onChange={(e) => setFormData({...formData, bank_account_number: e.target.value})}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="ifsc-upload">IFSC Code</Label>
                              <Input
                                id="ifsc-upload"
                                value={formData.ifsc_code}
                                onChange={(e) => setFormData({...formData, ifsc_code: e.target.value.toUpperCase()})}
                                maxLength={11}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="email-upload">Email</Label>
                              <Input
                                id="email-upload"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({...formData, email: e.target.value})}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="phone-upload">Phone</Label>
                              <Input
                                id="phone-upload"
                                value={formData.phone}
                                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => {
                              setDialogOpen(false);
                              resetForm();
                            }}>
                              Cancel
                            </Button>
                            <Button type="submit">
                              Save Vendor
                            </Button>
                          </DialogFooter>
                        </form>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Vendors List */}
      <Card data-testid="vendors-list-card" className="border-border">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading">All Vendors</CardTitle>
          <CardDescription>
            {searchTerm ? `Showing ${filteredVendors.length} of ${vendors.length} vendors` : `Total: ${vendors.length} vendors`}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          {/* KYC status filter chips */}
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="kyc-filter-chips">
            {[
              { key: 'all', label: 'All Vendors' },
              { key: 'complete', label: 'KYC Complete', tone: 'emerald' },
              { key: 'partial', label: 'KYC Partial', tone: 'amber' },
              { key: 'pending', label: 'KYC Pending', tone: 'rose' },
            ].map((chip) => {
              const active = kycFilter === chip.key;
              const toneCls = {
                emerald: active
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'border-emerald-200 text-emerald-800 bg-emerald-50 hover:bg-emerald-100',
                amber: active
                  ? 'bg-amber-600 text-white border-amber-600'
                  : 'border-amber-200 text-amber-800 bg-amber-50 hover:bg-amber-100',
                rose: active
                  ? 'bg-rose-600 text-white border-rose-600'
                  : 'border-rose-200 text-rose-800 bg-rose-50 hover:bg-rose-100',
                neutral: active
                  ? 'bg-foreground text-background border-foreground'
                  : 'border-border text-foreground bg-card hover:bg-muted',
              }[chip.tone || 'neutral'];
              return (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setKycFilter(chip.key)}
                  data-testid={`kyc-filter-${chip.key}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition ${toneCls}`}
                >
                  {chip.label}
                  <span className={`inline-flex items-center justify-center min-w-[18px] px-1 rounded-full text-[10px] font-bold ${active ? 'bg-white/20 text-white' : 'bg-foreground/10 text-foreground'}`}>
                    {kycCounts[chip.key] || 0}
                  </span>
                </button>
              );
            })}
          </div>
          {filteredVendors.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-muted-foreground">
                {searchTerm
                  ? 'No matching vendors found'
                  : kycFilter !== 'all'
                  ? `No vendors in '${kycFilter}' status`
                  : 'No vendors found'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {searchTerm
                  ? 'Try a different search term'
                  : kycFilter !== 'all'
                  ? 'Switch filter to view other vendors'
                  : 'Add vendors to use them in POs and invoices'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredVendors.map((vendor) => (
                <Card
                  key={vendor.id}
                  data-testid={`vendor-card-${vendor.id}`}
                  className="border-border hover:shadow-md transition-all"
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading font-semibold text-lg truncate">{vendor.name}</h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {vendor.gstin && (
                            <p className="text-xs text-muted-foreground">
                              GSTIN: {vendor.gstin}
                            </p>
                          )}
                          {(() => {
                            const status = vendor.kyc_status || 'pending';
                            const present = vendor.kyc_required_present ?? 0;
                            const total = vendor.kyc_required_total ?? 4;
                            const styles = {
                              complete: 'bg-emerald-50 border-emerald-200 text-emerald-800',
                              partial: 'bg-amber-50 border-amber-200 text-amber-800',
                              pending: 'bg-rose-50 border-rose-200 text-rose-800',
                            }[status];
                            const labelMap = {
                              complete: 'KYC Complete',
                              partial: 'KYC Partial',
                              pending: 'KYC Pending',
                            };
                            return (
                              <Badge
                                variant="outline"
                                className={`h-5 px-1.5 py-0 text-[10px] gap-1 ${styles}`}
                                data-testid={`vendor-kyc-status-${vendor.id}`}
                                title={`${present} of ${total} required KYC documents uploaded`}
                              >
                                <ShieldCheck className="h-3 w-3" />
                                {labelMap[status]} · {present}/{total}
                              </Badge>
                            );
                          })()}
                          {vendor.on_hold && (
                            <Badge
                              variant="outline"
                              className="h-5 px-1.5 py-0 text-[10px] gap-1 bg-rose-100 border-rose-300 text-rose-800"
                              data-testid={`vendor-hold-badge-${vendor.id}`}
                              title={vendor.on_hold_reason || 'Vendor on hold — invoices cannot be added to approvals'}
                            >
                              <Pause className="h-3 w-3" />
                              On Hold
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          title="View KYC & details"
                          onClick={() => navigate(`/vendors/${vendor.id}`)}
                          data-testid={`view-vendor-${vendor.id}`}
                        >
                          <Eye className="h-4 w-4 text-primary" />
                        </Button>
                        {hasTabAccess('vendors') && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggleHold(vendor)}
                              data-testid={`hold-vendor-${vendor.id}`}
                              title={vendor.on_hold ? 'Release from hold' : 'Place on hold'}
                            >
                              {vendor.on_hold ? (
                                <Play className="h-4 w-4 text-emerald-700" />
                              ) : (
                                <Pause className="h-4 w-4 text-amber-700" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(vendor)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(vendor.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      {vendor.pan && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">PAN:</span>
                          <span className="font-mono">{vendor.pan}</span>
                        </div>
                      )}
                      {vendor.email && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Email:</span>
                          <span className="text-xs truncate max-w-[150px]">{vendor.email}</span>
                        </div>
                      )}
                      {vendor.phone && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Phone:</span>
                          <span>{vendor.phone}</span>
                        </div>
                      )}
                      {vendor.bank_account_number && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Bank A/C:</span>
                          <span className="font-mono text-xs">***{vendor.bank_account_number.slice(-4)}</span>
                        </div>
                      )}
                      {vendor.ifsc_code && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">IFSC:</span>
                          <span className="font-mono">{vendor.ifsc_code}</span>
                        </div>
                      )}
                    </div>
                    {vendor.remarks && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-muted-foreground italic">
                          {vendor.remarks}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Vendors;
